// dashboardAttention.js — PURE selector for the operations Dashboard.
// Classifies every open item by RESPONSIBILITY (whose court) and, using simple date-based thresholds,
// PROMOTES the overdue / stale / at-risk ones into a single "Needs Attention Now" lane. No mutations,
// no queries, no AI — every rule is one date comparison against a named threshold below.
//
// One item appears in exactly one place: if promoted it shows ONLY in the attention lane; otherwise in its
// responsibility group. Group counts still report the total (incl. promoted) + an attention sub-count.
//
// Missing dates NEVER fabricate urgency: if the date a rule needs is absent, the item is not promoted — it
// stays in its group. (e.g. a Returned-for-Revision job with a null returned_at is shown, never flagged.)

import { displayState } from './workflow'
import { STAGES } from './pipeline'
import { quoteCompanyName } from './quoteTitle'
import { isOtherService } from './service'
import { isPortalJob, daysSinceApplyEmail } from './bizsafePortal'

// ── Thresholds — SINGLE source. Provisional MVP defaults (Winnie-approved 2026-07-13). Later: move to Settings. ──
export const THRESHOLDS = {
  staleClientWaitDays: 7,    // ack/signature link sent, no response
  wshcPendingDays: 14,       // submitted to WSHC, no outcome
  reportOverdueDays: 7,      // days after audit_date with no report
  revisionOverdueDays: 5,    // returned to auditor, not resubmitted
  quoteDraftAgingDays: 3,    // draft quote age (no needed-by date exists — created_at proxy)
  certAtRiskDays: 30,        // cert expires within N days and renewal not yet secured
  wshcWindowDays: 62,        // ~2 months to expiry = WSHC window open → unsecured / auditor-stuck renewals are urgent
  scheduleAtRiskDays: 45,    // cert expires within N days and no audit date booked
  dueSoonDays: 90,           // monitoring only (not attention)
  longDeadDays: 90,          // expired more than N days with no active job → lost/hold, never red
}

const DAY = 86400000
const RANK_SCHEDULED = STAGES.indexOf('Scheduled')

function toDate(s) { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d }
function ageDays(s, today) { const d = toDate(s); return d ? Math.floor((today - d) / DAY) : null }       // days SINCE
function untilDays(s, today) { const d = toDate(s); return d ? Math.floor((d - today) / DAY) : null }     // days UNTIL
function fmtDay(s) { const d = toDate(s); return d ? d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short' }) : '' }

// Order the attention lane: overdue before at-risk; within overdue most-overdue first; within at-risk soonest first.
function laneSort(a, b) {
  const t = { overdue: 0, atrisk: 1 }
  if (t[a.tier] !== t[b.tier]) return t[a.tier] - t[b.tier]
  return (a.sortVal ?? 9999) - (b.sortVal ?? 9999)
}
// Order within a group: soonest deadline / oldest wait first; unknown urgency last.
function groupSort(a, b) { return (a.sortVal ?? 9999) - (b.sortVal ?? 9999) }

export function deriveDashboard({
  jobs: allJobs = [], clients = [], preAuditActions = [], remarks = [], monitoringExtra = [],
  pipeRows = [], toPrepare = [], awaitingSign = [], today = new Date(),
} = {}) {
  // CLIENT INTERNAL AUDIT jobs are tracked on their own page/tracker — never in the bizSAFE attention list (which
  // would raise bizSAFE-expiry / WSHC alarms against them). "Leave out internal audit" keeps bizSAFE+SHMS as-is.
  const jobs = (allJobs || []).filter(j => !isOtherService(j.audit_type))
  const clientsById = new Map((clients || []).map(c => [c.id, c]))
  const activeClientIds = new Set((jobs || []).filter(j => !['Completed', 'Cancelled'].includes(j.status)).map(j => j.client_id).filter(Boolean))
  const coveredJobs = new Set()
  // Non-primary members of a combined audit are represented by the PRIMARY — never surface a linked (secondary)
  // company independently (attention, groups, or recent). Path-independent (manual or online sign-in).
  for (const j of (jobs || [])) if (j.combined_group_id && !j.is_combined_primary) coveredJobs.add(j.id)
  const all = []

  // Cert proximity for a client. atRisk = cert expires within certAtRiskDays (incl. already-lapsed) WHILE the
  // renewal outcome is still pending — i.e. the job is not yet fully resolved / finalised (not Completed). This
  // intentionally covers a job already submitted to WSHC whose cert is expiring (e.g. Cavill): the rule is
  // "cert at-risk while the outcome is still pending", NOT "not yet WSHC-submitted" (which would wrongly
  // exclude that exact case). Once the job is Completed the renewal is secured and it no longer flags.
  function cert(clientId) {
    const c = clientId ? clientsById.get(clientId) : null
    const du = c ? untilDays(c.bizsafe_expiry_date, today) : null
    return { du, expiry: c?.bizsafe_expiry_date || null }
  }
  const certRiskReason = (du, expiry) => du == null ? null
    : du < 0 ? `cert lapsed ${Math.abs(du)}d` : du <= THRESHOLDS.wshcWindowDays ? `cert expires ${fmtDay(expiry)} (${du}d)` : null

  const push = it => all.push(it)

  // ── 1) Post-submission jobs — classify by workflow displayState ──
  for (const j of (jobs || [])) {
    if (coveredJobs.has(j.id) || ['Completed', 'Cancelled'].includes(j.status)) continue   // skip linked combined members
    const ds = displayState(j)
    // Draft = auditor still preparing (their court, not office); Completed = closed; null = pre-audit (step 2).
    if (!ds || ds === 'Completed' || ds === 'Draft') continue
    coveredJobs.add(j.id)
    const client = j.clients?.name || j.quote_ref || '—'
    const { du, expiry } = cert(j.client_id)
    const atRisk = du != null && du <= THRESHOLDS.certAtRiskDays
    const nav = `/jobs/${j.id}`
    const base = { key: `job:${j.id}`, client, nav, sortVal: du != null ? du : 9999 }
    const reasons = []
    let tier = null

    // New WSHC client-portal process: the client applies on WSHC; AuditPM emails them the steps. Only real office
    // work reaches the desk (group A): no email for the person applying, or no reply 28 days after the email.
    if (isPortalJob(j) && (ds === 'Ready for WSHC Submission' || ds === 'Pending WSHC Outcome')) {
      if (ds === 'Ready for WSHC Submission') {
        if (!j.wshc_applicant_email) { base.group = 'A'; base.label = 'Add client’s assigned staff (applies on WSHC)'; base.meta = 'client signed' }
        else { base.group = 'D'; base.label = 'Client acknowledged'; base.meta = 'AuditPM emails the apply steps' }
      } else if (j.client_wshc_submitted_at) {
        base.group = 'D'; base.label = 'Awaiting WSHC outcome'; base.meta = `client says applied ${fmtDay(j.client_wshc_submitted_at)}`
      } else {
        const a = daysSinceApplyEmail(j)
        if (a != null && a >= 28) { base.group = 'A'; base.label = 'Call client — no reply after 28 days'; base.meta = `apply email ${fmtDay(j.wshc_apply_email_sent_at)}`; reasons.push(`no reply ${a}d`); tier = 'overdue'; base.sortVal = -a }
        else { base.group = 'D'; base.label = 'Awaiting WSHC outcome'; base.meta = j.wshc_apply_email_sent_at ? `steps emailed ${fmtDay(j.wshc_apply_email_sent_at)}` : '' }
      }
    }
    else if (ds === 'Pending Office Review') { base.group = 'A'; base.label = 'Review report'; base.meta = j.report_submitted_by ? `submitted by ${j.report_submitted_by}` : '' }
    else if (ds === 'Ready to Send Report') { base.group = 'A'; base.label = 'Send report to client'; base.meta = '' }
    else if (ds === 'Ready for WSHC Submission') { base.group = 'A'; base.label = 'Record WSHC submission'; base.meta = '' }
    else if (ds === 'Awaiting Client Acknowledgement') {
      base.group = 'B'; base.label = 'Awaiting client acknowledgement'; base.meta = j.report_ack_sent_at ? `sent ${fmtDay(j.report_ack_sent_at)}` : ''
      const a = ageDays(j.report_ack_sent_at, today)
      if (a != null && a > THRESHOLDS.staleClientWaitDays) { reasons.push(`awaiting client ${a}d`); tier = 'overdue'; base.sortVal = -a }
    }
    else if (ds === 'Pending WSHC Outcome') {
      base.group = 'D'; base.label = 'Awaiting WSHC outcome'; base.meta = j.submitted_to_wshc_at ? `submitted ${fmtDay(j.submitted_to_wshc_at)}` : ''
      const a = ageDays(j.submitted_to_wshc_at, today)
      if (a != null && a > THRESHOLDS.wshcPendingDays) { reasons.push(`WSHC pending ${a}d`); tier = 'overdue'; base.sortVal = -a }
    }
    else if (ds === 'Returned for Revision') {
      base.group = 'C'; base.label = 'Returned for revision'; base.meta = j.returned_at ? `returned ${fmtDay(j.returned_at)}` : ''
      const a = ageDays(j.returned_at, today)                    // null returned_at → never promoted
      if (a != null && a > THRESHOLDS.revisionOverdueDays) { reasons.push(`revision ${a}d overdue`); tier = 'overdue'; base.sortVal = -a }
    }
    else { base.group = 'A'; base.label = ds; base.meta = '' }

    if (atRisk) { const r = certRiskReason(du, expiry); if (r) { reasons.push(r); if (!tier) tier = 'atrisk'; base.sortVal = du } }
    if (reasons.length) { base.promoted = true; base.tier = tier || 'atrisk'; base.reason = reasons.join(' · ') }
    push(base)
  }

  // ── 2) Pre-audit action items (plan ack / COI / high-risk COI) — jobs with no workflow state yet ──
  const jobById = new Map((jobs || []).map(j => [j.id, j]))
  for (const a of (preAuditActions || [])) {
    if (coveredJobs.has(a.jobId)) continue
    const j = jobById.get(a.jobId) || {}
    coveredJobs.add(a.jobId)
    const { du, expiry } = cert(j.client_id)
    const atRisk = du != null && du <= THRESHOLDS.certAtRiskDays
    // Default sort = days until the AUDIT (soonest audit first) — a pre-audit COI/ack item's urgency is the
    // upcoming audit date, not the cert expiry. Overridden below to -age when stale, or to cert days when at-risk.
    const auditIn = untilDays(a.auditDate, today)
    const base = { key: `pa:${a.jobId}`, client: a.clientName || '—', nav: `/jobs/${a.jobId}`, sortVal: auditIn != null ? auditIn : 9999 }
    const reasons = []; let tier = null
    if (/^High-risk COI/i.test(a.issue)) { base.group = 'A'; base.label = a.issue; base.meta = ''; base.promoted = true; base.tier = 'overdue'; base.reason = 'blocking — resolve now'; base.sortVal = -9999; push(base); continue }
    if (/acknowledg/i.test(a.issue)) {
      base.group = 'B'; base.label = 'Client plan acknowledgement pending'; base.meta = a.auditDate ? `audit ${fmtDay(a.auditDate)}` : ''
      const sent = j.plan_sent_date || j.audit_plan_sent_at
      const age = ageDays(sent, today)
      if (age != null && age > THRESHOLDS.staleClientWaitDays) { reasons.push(`unacknowledged ${age}d`); tier = 'overdue'; base.sortVal = -age }
    } else { base.group = 'C'; base.label = a.issue; base.meta = a.auditDate ? `audit ${fmtDay(a.auditDate)}` : '' }  // COI pending → waiting on auditor
    if (atRisk) { const r = certRiskReason(du, expiry); if (r) { reasons.push(r); if (!tier) tier = 'atrisk'; base.sortVal = du } }
    if (reasons.length) { base.promoted = true; base.tier = tier || 'atrisk'; base.reason = reasons.join(' · ') }
    push(base)
  }

  // ── 3) Sales / pipeline items (only for clients/jobs not already owned by a job above) ──
  for (const q of (toPrepare || [])) {                      // Draft quotes → prepare (office action)
    const age = ageDays(q.created_at, today)
    const it = { key: `prep:${q.id}`, group: 'A', client: quoteCompanyName(q, q.clients) || '—', label: 'Prepare quotation', meta: q.audit_type || '', nav: '/quotations', sortVal: age != null ? -age : 9999 }
    if (age != null && age > THRESHOLDS.quoteDraftAgingDays) { it.promoted = true; it.tier = 'overdue'; it.reason = `draft quote ${age}d old` }
    push(it)
  }
  for (const q of (awaitingSign || [])) {                   // Sent quotes → awaiting client signature
    const age = ageDays(q.sent_date, today)
    const { du, expiry } = isOtherService(q.audit_type) ? { du: null, expiry: null } : cert(q.client_id)   // no bizSAFE-expiry alarm on IA quotes
    const it = { key: `sign:${q.id}`, group: 'B', client: quoteCompanyName(q, q.clients) || '—', label: 'Awaiting quotation signature', meta: q.sent_date ? `sent ${fmtDay(q.sent_date)}` : '', nav: '/quotations', sortVal: age != null ? -age : 9999 }
    const reasons = []; let tier = null
    if (age != null && age > THRESHOLDS.staleClientWaitDays) { reasons.push(`unsigned ${age}d`); tier = 'overdue' }
    if (du != null && du <= THRESHOLDS.wshcWindowDays) { const r = certRiskReason(du, expiry); if (r) { reasons.push(r); if (!tier) tier = 'atrisk'; it.sortVal = du } }
    if (reasons.length) { it.promoted = true; it.tier = tier; it.reason = reasons.join(' · ') }
    push(it)
  }
  for (const r of (pipeRows || [])) {
    if (r.jobId && coveredJobs.has(r.jobId)) continue
    if (r.stage === 'Signed' && !r.auditDate) {             // committed, no audit booked → schedule (office)
      if (r.jobId) coveredJobs.add(r.jobId)
      const it = { key: `sched:${r.key}`, group: 'A', client: r.client, label: 'Schedule audit date', meta: r.daysToExpiry != null ? `expiry ${fmtDay(r.expiry)}` : '', nav: '/pipeline', sortVal: r.daysToExpiry != null ? r.daysToExpiry : 9999 }
      if (r.daysToExpiry != null && r.daysToExpiry <= THRESHOLDS.scheduleAtRiskDays) { it.promoted = true; it.tier = 'atrisk'; it.reason = `${certRiskReason(r.daysToExpiry, r.expiry) || `expires ${fmtDay(r.expiry)}`} · no audit date` }
      push(it)
    } else if (r.stage === 'Audit done' && r.postAudit === 'Awaiting report') {   // auditor owes the report
      if (r.jobId) coveredJobs.add(r.jobId)
      const age = ageDays(r.auditDate, today)
      const it = { key: `rep:${r.key}`, group: 'C', client: r.client, label: 'Report not received', meta: r.auditDate ? `audited ${fmtDay(r.auditDate)}` : '', nav: r.jobId ? `/jobs/${r.jobId}` : '/pipeline', sortVal: age != null ? -age : 9999 }
      const reasons = []; let tier = null
      if (age != null && age > THRESHOLDS.reportOverdueDays) { reasons.push(`report ${age}d overdue`); tier = 'overdue'; it.sortVal = -age }
      if (r.daysToExpiry != null && r.daysToExpiry <= THRESHOLDS.wshcWindowDays) { const cr = certRiskReason(r.daysToExpiry, r.expiry); if (cr) { reasons.push(cr); if (!tier) tier = 'atrisk'; it.sortVal = r.daysToExpiry } }   // report stuck with auditor while the WSHC window is open
      if (reasons.length) { it.promoted = true; it.tier = tier; it.reason = reasons.join(' · ') }
      push(it)
    } else if (r.type === 'Renewal' && r.stageRank < RANK_SCHEDULED && !['On hold', 'No response'].includes(r.salesOutcome)) {
      if (r.clientId && activeClientIds.has(r.clientId)) continue    // client already has an active job elsewhere
      const d = r.daysToExpiry
      if (d == null) continue
      if (d < -THRESHOLDS.longDeadDays) continue    // long-dead handled by the direct clients scan below (dedup)
      const it = { key: `ren:${r.key}`, group: 'E', client: r.client, nav: '/pipeline?view=renewals', sortVal: d }
      if (d <= THRESHOLDS.wshcWindowDays) { it.subtype = 'dueSoon'; it.label = 'Renewal — nothing scheduled'; it.meta = d < 0 ? `overdue ${Math.abs(d)}d` : `expires ${fmtDay(r.expiry)} (${d}d)`; it.promoted = true; it.tier = 'atrisk'; it.reason = `${it.meta} · not scheduled` }
      else if (d <= THRESHOLDS.dueSoonDays) { it.subtype = 'dueSoon'; it.label = 'Renewal due soon'; it.meta = `expires ${fmtDay(r.expiry)} (${d}d)` }
      else continue
      push(it)
    }
  }
  // Long-dead certs (expired > longDeadDays) with no active job — quiet LOST/HOLD review, never promoted, never
  // red. Sourced directly from clients (the ±6-month pipeline horizon would otherwise drop them entirely).
  for (const c of (clients || [])) {
    const du = untilDays(c.bizsafe_expiry_date, today)
    if (du == null || du >= -THRESHOLDS.longDeadDays) continue
    if (activeClientIds.has(c.id)) continue
    push({ key: `dead:${c.id}`, group: 'E', subtype: 'lostHold', client: c.name, label: 'Long expired — no active job', meta: `expired ${fmtDay(c.bizsafe_expiry_date)}`, nav: '/clients', sortVal: 9999 })
  }

  // ── 4) Watchlist (client remarks on a valid acknowledgement) + Recent activity (healthy, not surfaced) ──
  for (const a of (remarks || [])) {
    if (coveredJobs.has(a.jobId)) continue           // already shown as a work item → don't repeat the remark
    coveredJobs.add(a.jobId)
    push({ key: `rm:${a.jobId}`, group: 'E', subtype: 'watchlist', client: a.clientName, label: `Remark: “${a.remark}”`, meta: '', nav: `/jobs/${a.jobId}`, sortVal: 9999 })
  }
  for (const m of (monitoringExtra || [])) push({ key: m.key, group: 'E', subtype: 'watchlist', client: m.client, label: m.label, meta: m.meta || '', nav: m.nav || '/', sortVal: 9999 })
  const recent = (jobs || [])
    .filter(j => !['Completed', 'Cancelled'].includes(j.status) && !coveredJobs.has(j.id))
    .slice(0, 6)
    .map(j => ({ key: `rec:${j.id}`, group: 'F', client: j.clients?.name || '—', label: j.audit_type || '', meta: j.audit_date ? fmtDay(j.audit_date) : '', status: j.status, nav: `/jobs/${j.id}`, sortVal: 0 }))
  recent.forEach(push)

  // ── Assemble ──
  const attention = all.filter(x => x.promoted).sort(laneSort)
  const groupOf = letter => all.filter(x => x.group === letter && !x.promoted).sort(groupSort)
  const count = letter => ({ total: all.filter(x => x.group === letter).length, attn: all.filter(x => x.group === letter && x.promoted).length })
  return {
    attention,
    groups: {
      needsAction: groupOf('A'), waitingClient: groupOf('B'), waitingAuditor: groupOf('C'),
      waitingWSHC: groupOf('D'), monitoring: groupOf('E'), recent: groupOf('F'),
    },
    counts: { needsAction: count('A'), waitingClient: count('B'), waitingAuditor: count('C'), waitingWSHC: count('D'), monitoring: count('E') },
  }
}
